// Customer types, admin side (BRIEF-customer-types-zones-picksheet.md §2a, Thread B2).
//
// The server resolves every menu and price (server/services/menuPricing.ts); nothing
// here decides what a customer sees or pays. These helpers only PREVIEW the server's
// rules on the admin screens — how many products a type hides, the greyed price a
// blank type-price cell inherits — and tests/client/customer-types.test.ts pins them
// to the server resolver so the preview never drifts from what customers get.
import { isShownState, menuStateFrom, ruleForTick, type TypeMenuFacts, type TypeMenuState } from "@shared/customerTypeMenu";
import type { CustomerTypePriceMode } from "@shared/schema";

/** GET /customer-types row. */
export interface CustomerTypeSummary {
  id: number;
  name: string;
  sortOrder: number;
  /** Stored even when unused: it applies only when priceMode is 'percent'. */
  priceAdjustPercent: number | null;
  /** base = the master menu's prices; own = this type's price list; percent = base ± %. */
  priceMode: CustomerTypePriceMode;
  /** No delivery charge on this type's accounts' orders (organizations.delivery_fee_product_id). */
  deliveryFeeExempt: boolean;
  accountCount: number;
  /** Products and categories the type hides. */
  ruleCount: number;
  priceCount: number;
}

/**
 * One row of customer_type_menu_rules: it hides one product or one whole category. The
 * server sends hide rules only; `hidden: false` (the retired "show anyway") means nothing.
 */
export interface CustomerTypeRule {
  productId: number | null;
  categoryId: number | null;
  hidden: boolean;
}

/** GET /customer-types/:id. */
export interface CustomerTypeDetail extends Omit<CustomerTypeSummary, "accountCount" | "ruleCount" | "priceCount"> {
  rules: CustomerTypeRule[];
  prices: { productId: number; price: string }[];
}

/** What a type's visibility rules read from a product. */
export interface TypedProduct {
  id: number;
  categoryId: number | null;
  /** Not read: a type never hides by sell unit. Kept so callers can pass a product as is. */
  unit?: string | null;
}

export const CUSTOMER_TYPES_PATH = "/api/customer-types";
export const customerTypePath = (id: number) => `${CUSTOMER_TYPES_PATH}/${id}`;
/** PUT { shown } — the product form's tick: one product, one type. */
export const productCustomerTypePath = (productId: number, typeId: number) =>
  `/api/products/${productId}/customer-types/${typeId}`;

type TypeMenuRules = Pick<CustomerTypeDetail, "rules">;

/** What decides one product's place on one type's menu — as menuPricing.typeMenuState reads it. */
export function typeMenuFacts(product: TypedProduct, type: TypeMenuRules): TypeMenuFacts {
  let rule: TypeMenuFacts["rule"] = null;
  let categoryHidden = false;
  for (const r of type.rules) {
    if (!r.hidden) continue;
    if (r.productId != null) {
      if (r.productId === product.id) rule = "hide";
    } else if (r.categoryId != null && r.categoryId === product.categoryId) {
      categoryHidden = true;
    }
  }
  return { categoryHidden, rule };
}

/** Where a product stands on a type's menu, and why — as menuPricing.typeMenuState. */
export function typeMenuState(product: TypedProduct, type: TypeMenuRules): TypeMenuState {
  return menuStateFrom(typeMenuFacts(product, type));
}

/** The type's own rules only (products, categories) — as menuPricing.isVisibleForType. */
export function isHiddenByType(product: TypedProduct, type: TypeMenuRules): boolean {
  return !isShownState(typeMenuState(product, type));
}

/** One "Shown to <type>" tick on the product form, as POST /products `customerTypes` takes it. */
export interface ProductTypeTick {
  customerTypeId: number;
  shown: boolean;
}

/**
 * The product form's tick for one type: where the product stands now, and where it
 * will stand once the admin's choice (if any) is saved. The server makes the same
 * call when it saves (ruleForTick), so the form previews exactly what it will store.
 */
export function previewTypeTick(
  product: TypedProduct,
  type: TypeMenuRules,
  choice: boolean | undefined,
): { state: TypeMenuState; changed: boolean } {
  const facts = typeMenuFacts(product, type);
  const tick = choice === undefined ? null : ruleForTick(facts, choice);
  if (!tick || "refused" in tick) return { state: menuStateFrom(facts), changed: false };
  return { state: menuStateFrom({ ...facts, rule: tick.rule }), changed: tick.rule !== facts.rule };
}

/**
 * The ticks worth sending when the product form saves: the admin's choices that change
 * something. A choice the product's category now overrules, or one that already stands,
 * is left out. `typeOf` returns a type's rules, or undefined while they are not loaded.
 */
export function pendingTypeTicks(
  product: TypedProduct,
  choices: Readonly<Record<number, boolean>>,
  typeOf: (customerTypeId: number) => TypeMenuRules | undefined,
): ProductTypeTick[] {
  const ticks: ProductTypeTick[] = [];
  for (const [key, shown] of Object.entries(choices)) {
    const customerTypeId = Number(key);
    const type = typeOf(customerTypeId);
    if (type && previewTypeTick(product, type, shown).changed) ticks.push({ customerTypeId, shown });
  }
  return ticks;
}

/** The type hides this whole category (a category rule). */
export function typeHidesCategory(categoryId: number, type: Pick<CustomerTypeDetail, "rules">): boolean {
  return type.rules.some((rule) => rule.hidden && rule.categoryId === categoryId);
}

/**
 * The account's own menu switches (accounts.custom_menu): hidden only on an explicit
 * `false`. As menuPricing.isAllowedByAccountMenu — the server ANDs this with the type,
 * so a switch can hide more than the type, never bring back what it hides.
 */
export function isAllowedByAccountMenu(
  product: TypedProduct,
  customMenu: { products?: Record<string, boolean>; categories?: Record<string, boolean> } | null | undefined,
): boolean {
  if (!customMenu) return true;
  const productEnabled = customMenu.products?.[product.id.toString()] !== false;
  const categoryEnabled = product.categoryId == null || customMenu.categories?.[product.categoryId.toString()] !== false;
  return productEnabled && categoryEnabled;
}

/**
 * What a product with no explicit type price costs a customer of this type:
 * the base price moved by the type's percent, or the base price itself.
 * As menuPricing.resolvePrice with no explicit price.
 */
export function inheritedTypePrice(basePrice: string | null | undefined, priceAdjustPercent: number | null): string | null {
  const base = basePrice ?? null;
  if (priceAdjustPercent == null || base == null || base.trim() === "") return base;
  const amount = Number(base);
  if (!Number.isFinite(amount)) return base;
  return (Math.round((amount * (1 + priceAdjustPercent / 100) + Number.EPSILON) * 100) / 100).toFixed(2);
}

/**
 * What a customer of this type pays for one product — the price an order line takes when
 * staff leave its price blank. As menuPricing.resolvePrice with the type loaded the way
 * loadCustomerTypes loads it: the type's own price list only in 'own' mode, its percent
 * only in 'percent' mode, the base price in 'base' mode or with no type at all.
 * The staff order screens grey it in a line's price box (Pitt, 2026-10-24: a Grocer
 * account's lines showed the restaurant price while the order was being built).
 */
export function accountMenuPrice(
  product: { id: number; price?: string | null },
  type: Pick<CustomerTypeDetail, "priceMode" | "priceAdjustPercent" | "prices"> | null | undefined,
): string | null {
  const base = product.price ?? null;
  if (!type) return base;
  if (type.priceMode === "own") {
    const own = type.prices.find((p) => p.productId === product.id)?.price?.trim();
    return own ? own : base;
  }
  if (type.priceMode === "percent") return inheritedTypePrice(base, type.priceAdjustPercent);
  return base;
}

/**
 * A price cell as typed → what PUT /customer-types/:id/prices accepts.
 * Blank clears the explicit price (the product inherits again). The server's rule:
 * up to 7 digits, up to 2 decimals, more than zero.
 */
export function parseTypePriceInput(raw: string): { ok: true; price: string | null } | { ok: false; message: string } {
  let cleaned = raw.replace(/[\s$]/g, "");
  if (cleaned === "") return { ok: true, price: null };
  // A comma only as a thousands separator ("1,250.00"). "4,50" is refused, never read as 450.
  if (cleaned.includes(",")) {
    if (!/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(cleaned)) {
      return { ok: false, message: "Use a point for cents, like 4.50." };
    }
    cleaned = cleaned.replace(/,/g, "");
  }
  const normalised = cleaned.startsWith(".") ? `0${cleaned}` : cleaned;
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(normalised)) {
    return { ok: false, message: "A price is a number with up to two decimals, like 42 or 42.50." };
  }
  if (!(Number(normalised) > 0)) return { ok: false, message: "A price must be more than zero." };
  return { ok: true, price: normalised };
}

/** "−7%" / "+5%" for a percent adjustment. */
export function formatPercent(pct: number): string {
  return `${pct < 0 ? "−" : "+"}${Math.abs(pct)}%`;
}
